/**
 * Utility functions for consistent date handling across the application
 */
import { DateTime } from 'luxon';

/**
 * Formats a date as YYYY-MM-DD, preserving the local date regardless of timezone
 * @param date The date to format (can be a Date object or string)
 * @returns A string in YYYY-MM-DD format representing the local date
 */
export function formatDateAsYYYYMMDD(date: Date | string | null): string | null {
  if (!date) return null;
  
  // If it's already in YYYY-MM-DD format, just return it
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  // Convert to Luxon DateTime
  const dt = typeof date === 'string' 
    ? DateTime.fromISO(date) 
    : DateTime.fromJSDate(date);
  
  // Create a normalized DateTime at noon UTC to avoid timezone issues
  const normalized = DateTime.utc(
    dt.year,
    dt.month,
    dt.day,
    12, 0, 0
  );
  
  // Format as YYYY-MM-DD
  return normalized.toFormat('yyyy-MM-dd');
}

/**
 * Parses a date string in YYYY-MM-DD format to a Date object
 * @param dateString The date string to parse
 * @returns A Date object representing the local date
 */
export function parseYYYYMMDDToDate(dateString: string | null): Date | null {
  if (!dateString) return null;
  
  // Use Luxon to parse the date string
  const dt = DateTime.fromFormat(dateString, 'yyyy-MM-dd');
  
  // Set to noon UTC to avoid timezone issues
  const normalized = DateTime.utc(
    dt.year,
    dt.month,
    dt.day,
    12, 0, 0
  );
  
  // Convert back to JS Date
  return normalized.toJSDate();
}
